import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, CircularProgress, Typography } from "@mui/material";
import type { Story } from "@theirstory/common/api_types/backend";

import { isStoryEligibleForAIGeneration } from "../Project/Modals/aiGenerationUtils";

import { getContentStatus, getMissingRequirements, type ContentStatus } from "./portalStoryContentStatus";

const rowGridColumns = "minmax(220px, 1fr) 150px 120px";

export const ContentStatusLabel = ({ status, unavailable }: { status: ContentStatus; unavailable?: boolean }) => {
  if (status === "ready") {
    return (
      <Box sx={{ alignItems: "center", color: "#239B8B", display: "inline-flex" }}>
        <CheckCircleIcon sx={{ fontSize: 18 }} />
      </Box>
    );
  }

  if (status === "queued") {
    return (
      <Box sx={{ alignItems: "center", color: "#0874CE", display: "inline-flex" }}>
        <CircularProgress size={16} thickness={5} sx={{ color: "#0874CE" }} />
      </Box>
    );
  }

  if (unavailable) {
    return <Typography sx={{ color: "#606971", fontSize: "12px", lineHeight: "18px" }}>—</Typography>;
  }

  return <Typography sx={{ color: "#606971", fontSize: "12px", lineHeight: "18px" }}>To be generated</Typography>;
};

interface BulkStoryRowProps {
  story: Story | undefined;
  isAlreadyIn: boolean;
}

const BulkStoryRow = ({ story, isAlreadyIn }: BulkStoryRowProps) => {
  const descriptionStatus = story ? getContentStatus(story, "description", []) : "missing";
  const indexStatus = story ? getContentStatus(story, "index", []) : "missing";
  const canGenerateAI = Boolean(story && isStoryEligibleForAIGeneration(story));

  return (
    <Box
      sx={{
        alignItems: "center",
        borderBottom: "1px solid #EEF0F2",
        display: "grid",
        gridTemplateColumns: rowGridColumns,
        minWidth: 560,
        opacity: isAlreadyIn ? 0.5 : 1,
        px: "12px",
        py: "9px",
        "&:last-child": { borderBottom: 0 },
      }}
    >
      <Box sx={{ minWidth: 0, pr: "12px" }}>
        <Typography
          sx={{
            color: "#464C53",
            fontSize: "13px",
            fontWeight: 600,
            lineHeight: "20px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {story?.title || "Untitled interview"}
        </Typography>
        {isAlreadyIn ? (
          <Typography sx={{ color: "#606971", fontSize: "12px", lineHeight: "18px" }}>Already in collection</Typography>
        ) : (
          story &&
          !canGenerateAI &&
          getMissingRequirements(story).length > 0 && (
            <Typography sx={{ color: "#C94343", fontSize: "12px", lineHeight: "18px" }}>Transcript required</Typography>
          )
        )}
      </Box>
      {isAlreadyIn ? (
        <Box sx={{ gridColumn: "span 2" }} />
      ) : (
        <>
          <ContentStatusLabel status={descriptionStatus} unavailable={!canGenerateAI} />
          <ContentStatusLabel status={indexStatus} unavailable={!canGenerateAI} />
        </>
      )}
    </Box>
  );
};

interface BulkStoryContentListProps {
  storyIds: string[];
  storiesById: Record<string, Story>;
  alreadyInCollectionIds: Set<string>;
  addableCount: number;
  skippedCount: number;
}

export const BulkStoryContentList = ({
  storyIds,
  storiesById,
  alreadyInCollectionIds,
  addableCount,
  skippedCount,
}: BulkStoryContentListProps) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      border: "1px solid #E5E7EA",
      borderRadius: "6px",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        borderBottom: "1px solid #E5E7EA",
        minWidth: 560,
        px: "12px",
        py: "10px",
        flexShrink: 0,
      }}
    >
      <Typography sx={{ color: "#464C53", fontSize: "13px", fontWeight: 700, lineHeight: "20px" }}>
        {storyIds.length} {storyIds.length === 1 ? "interview" : "interviews"} selected
      </Typography>
      <Typography sx={{ color: "#606971", fontSize: "12px", lineHeight: "18px" }}>
        {addableCount} ready to add
        {skippedCount > 0 ? ` · ${skippedCount} skipped (missing transcript)` : ""}
      </Typography>
    </Box>
    <Box
      sx={{
        alignItems: "center",
        backgroundColor: "#F7F9FC",
        borderBottom: "1px solid #E5E7EA",
        display: "grid",
        gridTemplateColumns: rowGridColumns,
        minWidth: 560,
        px: "12px",
        py: "8px",
        flexShrink: 0,
      }}
    >
      <Typography sx={{ color: "#606971", fontSize: "12px", fontWeight: 700, lineHeight: "18px" }}>
        Interview
      </Typography>
      <Typography sx={{ color: "#606971", fontSize: "12px", fontWeight: 700, lineHeight: "18px" }}>
        Description
      </Typography>
      <Typography sx={{ color: "#606971", fontSize: "12px", fontWeight: 700, lineHeight: "18px" }}>Index</Typography>
    </Box>
    <Box sx={{ maxHeight: 170, overflowY: "auto", overflowX: "hidden", minWidth: 560 }}>
      {storyIds.map((id) => (
        <BulkStoryRow key={id} story={storiesById[id]} isAlreadyIn={alreadyInCollectionIds.has(id)} />
      ))}
    </Box>
  </Box>
);
